import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { usersApi } from "../../api/users";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const AUDIT_SEVERITY = {
  critical: { label: "Crítica", color: "error", order: 0 },
  high: { label: "Alta", color: "error", order: 1 },
  medium: { label: "Media", color: "warning", order: 2 },
  low: { label: "Baja", color: "info", order: 3 },
  info: { label: "Informativa", color: "default", order: 4 },
};

const AUDIT_CATEGORY_LABELS = {
  project: "Proyecto",
  tenant: "Tenant",
  deployment: "Deployment",
  domain: "Dominio",
  erp: "ERP",
};

function normalizeIntegrityAuditResponse(payload) {
  const candidate =
    payload?.audit ||
    payload?.integrityAudit ||
    payload?.latestRun ||
    payload?.run ||
    (Array.isArray(payload?.runs) ? payload.runs[0] : null) ||
    payload?.data ||
    payload;

  if (!candidate || typeof candidate !== "object") return null;

  const findings = Array.isArray(candidate.findings)
    ? candidate.findings
    : Array.isArray(payload?.findings)
      ? payload.findings
      : [];
  const summary = candidate.summary || payload?.summary || {};
  const hasAuditData =
    findings.length > 0 ||
    candidate.id ||
    candidate.startedAt ||
    candidate.finishedAt ||
    Number(summary.findingsTotal || 0) > 0;

  return hasAuditData ? { ...candidate, findings, summary } : null;
}

function formatAuditDate(value) {
  if (!value) return "Fecha no informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no informada";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function evidenceProjectKey(finding) {
  const project = Array.isArray(finding?.evidence?.projects)
    ? finding.evidence.projects[0]
    : null;
  if (project?.slug || project?.id) return project.slug || project.id;
  if (Array.isArray(finding?.evidence?.projectIds)) return finding.evidence.projectIds[0] || "";
  return "";
}

function findingProjectLabel(finding) {
  if (finding?.projectSlug) return `/${finding.projectSlug}`;
  if (finding?.projectName) return finding.projectName;
  if (finding?.projectId) return `Proyecto ${finding.projectId}`;

  const evidenceProjects = Array.isArray(finding?.evidence?.projects)
    ? finding.evidence.projects
    : [];
  if (evidenceProjects.length) {
    const labels = evidenceProjects
      .slice(0, 2)
      .map((project) => project?.slug ? `/${project.slug}` : project?.id)
      .filter(Boolean);
    if (labels.length) return labels.join(" y ");
  }

  const evidenceIds = Array.isArray(finding?.evidence?.projectIds)
    ? finding.evidence.projectIds.filter(Boolean)
    : [];
  if (evidenceIds.length) return `${evidenceIds.length} proyectos`;
  if (finding?.dbName) return finding.dbName;
  return "Alcance global";
}

function findingResourceLink(finding) {
  if (finding?.code === "DEPLOYMENT_JOB_ORPHANED") {
    return { path: "/dashboard/proyectos", label: "Ver proyectos" };
  }

  const projectKey = finding?.projectSlug || finding?.projectId || evidenceProjectKey(finding);
  if (!projectKey) return null;

  const base = `/dashboard/proyectos/${encodeURIComponent(projectKey)}`;
  const category = String(finding?.category || "").toLowerCase();
  const resourceType = String(finding?.resourceType || "").toLowerCase();

  if (category === "erp") {
    if (resourceType.includes("stock") || resourceType.includes("product")) {
      return { path: `${base}/sistema/stock`, label: "Abrir stock" };
    }
    if (resourceType.includes("cash")) {
      return { path: `${base}/sistema/caja`, label: "Abrir caja" };
    }
    if (
      resourceType.includes("ledger") ||
      resourceType.includes("account") ||
      resourceType.includes("entry")
    ) {
      return { path: `${base}/sistema/ledger`, label: "Abrir libro" };
    }
    if (resourceType.includes("movement")) {
      return { path: `${base}/sistema/movimientos`, label: "Abrir movimientos" };
    }
    return { path: `${base}/sistema`, label: "Abrir sistema" };
  }

  if (["domain", "deployment"].includes(category)) {
    return { path: `${base}/configuracion/general?area=domain`, label: "Abrir publicación" };
  }

  if (category === "tenant") {
    return { path: `${base}/configuracion/general`, label: "Abrir configuración" };
  }

  return { path: base, label: "Abrir proyecto" };
}

function IntegrityAuditSection({ audit, loading, error, onReload, onNavigate }) {
  const findings = React.useMemo(
    () =>
      [...(audit?.findings || [])].sort((left, right) => {
        const leftOrder = AUDIT_SEVERITY[left?.severity]?.order ?? 99;
        const rightOrder = AUDIT_SEVERITY[right?.severity]?.order ?? 99;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return new Date(right?.detectedAt || 0).getTime() - new Date(left?.detectedAt || 0).getTime();
      }),
    [audit],
  );
  const summary = audit?.summary || {};
  const total = Number(summary.findingsTotal ?? findings.length) || findings.length;
  const auditDate = audit?.finishedAt || audit?.startedAt || audit?.createdAt;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        border: "1px solid rgba(25,36,47,0.08)",
        minWidth: 0,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: "rgba(243,90,58,0.1)",
                color: BRAND.orange,
              }}
            >
              <FactCheckRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.2, fontWeight: 950, color: BRAND.dark }}>
                Auditorías de integridad
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.45, fontSize: { xs: ".82rem", sm: ".95rem" }, lineHeight: 1.45 }}>
                Hallazgos persistidos por la revisión automática. Esta vista informa inconsistencias y sus causas sin corregir datos.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onReload}
            disabled={loading}
            aria-label="Actualizar auditorías de integridad"
            sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, border: "1px solid rgba(25,36,47,0.12)" }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Stack>

        {loading && <Alert severity="info" sx={{ borderRadius: 3 }}>Cargando auditorías...</Alert>}
        {!loading && error && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 3 }}
            action={<Button color="inherit" onClick={onReload} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && !audit && (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            Todavía no hay una auditoría de integridad disponible.
          </Alert>
        )}

        {!loading && !error && audit && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                label={`${total} hallazgo${total === 1 ? "" : "s"}`}
                color={total > 0 ? "warning" : "success"}
                sx={{ fontWeight: 850 }}
              />
              <Chip size="small" variant="outlined" label={`Última revisión: ${formatAuditDate(auditDate)}`} sx={{ fontWeight: 750 }} />
              {summary.truncated ? <Chip size="small" color="info" label="Listado parcial" sx={{ fontWeight: 850 }} /> : null}
            </Stack>

            {findings.length === 0 ? (
              <Alert severity="success" sx={{ borderRadius: 3 }}>
                La última auditoría no registró hallazgos.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {findings.map((finding, index) => {
                  const severity = AUDIT_SEVERITY[finding?.severity] || AUDIT_SEVERITY.info;
                  const resource = findingResourceLink(finding);
                  const key = finding?.id || `${finding?.code || "finding"}-${finding?.resourceId || index}-${index}`;

                  return (
                    <Box
                      key={key}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        bgcolor: "#F6F7FB",
                        border: "1px solid rgba(25,36,47,0.06)",
                        minWidth: 0,
                      }}
                    >
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
                            <Chip size="small" label={severity.label} color={severity.color} sx={{ fontWeight: 900 }} />
                            <Chip size="small" variant="outlined" label={findingProjectLabel(finding)} sx={{ maxWidth: "100%", fontWeight: 800 }} />
                            {finding?.category ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={AUDIT_CATEGORY_LABELS[finding.category] || finding.category}
                              />
                            ) : null}
                          </Stack>
                          <Typography sx={{ mt: 1, fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>
                            {finding?.message || "Se detectó una inconsistencia"}
                          </Typography>
                          {finding?.cause ? (
                            <Typography sx={{ mt: 0.45, color: "rgba(25,36,47,0.68)", fontSize: { xs: ".82rem", sm: ".9rem" }, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                              Causa: {finding.cause}
                            </Typography>
                          ) : null}
                          <Typography sx={{ mt: 0.75, color: "rgba(25,36,47,0.52)", fontSize: ".78rem", overflowWrap: "anywhere" }}>
                            {formatAuditDate(finding?.detectedAt || auditDate)}
                            {finding?.resourceType ? ` · ${finding.resourceType}` : ""}
                            {finding?.resourceId ? ` · ${finding.resourceId}` : ""}
                          </Typography>
                        </Box>
                        {resource ? (
                          <Button
                            onClick={() => onNavigate(resource.path)}
                            endIcon={<ArrowForwardRoundedIcon />}
                            sx={{
                              width: { xs: "100%", md: "auto" },
                              alignSelf: { xs: "stretch", md: "center" },
                              color: BRAND.orange,
                              textTransform: "none",
                              fontWeight: 900,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {resource.label}
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCard({ icon, title, value, helper }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        border: "1px solid rgba(25,36,47,0.08)",
        height: "100%",
        minWidth: 0,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
        <Stack direction="row" spacing={{ xs: 1.35, sm: 2 }} alignItems="center">
          <Box
            sx={{
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              borderRadius: { xs: 2.5, sm: 3 },
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: "rgba(243,90,58,0.1)",
              color: BRAND.orange,
              "& svg": { fontSize: { xs: 23, sm: 24 } },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "rgba(25,36,47,0.62)", fontSize: { xs: ".8rem", sm: ".9rem" }, fontWeight: 850 }}>
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.1,
                color: BRAND.dark,
                fontSize: { xs: "1.55rem", sm: "1.8rem", md: "2.05rem" },
                lineHeight: 1.1,
                fontWeight: 950,
                overflowWrap: "anywhere",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
        {helper && (
          <Typography sx={{ mt: { xs: 1.25, sm: 2 }, color: "rgba(25,36,47,0.62)", fontSize: { xs: ".78rem", sm: ".9rem" }, lineHeight: 1.5 }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function SuperPanelPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [approvingId, setApprovingId] = React.useState("");
  const [integrityAudit, setIntegrityAudit] = React.useState(null);
  const [auditLoading, setAuditLoading] = React.useState(true);
  const [auditError, setAuditError] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await adminApi.overview();
      setOverview(data.overview);
    } catch (err) {
      setError(err.message || "No se pudo cargar el superpanel");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadIntegrityAudit = React.useCallback(async () => {
    try {
      setAuditLoading(true);
      setAuditError("");
      const data = await adminApi.integrityAudit();
      setIntegrityAudit(normalizeIntegrityAuditResponse(data));
    } catch (err) {
      setAuditError(err.message || "No se pudieron cargar las auditorías de integridad");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadIntegrityAudit();
  }, [loadIntegrityAudit]);

  const approveAsClient = async (user) => {
    try {
      setApprovingId(user.id);
      setError("");
      await usersApi.approve(user.id, { globalRole: "client" });
      await loadOverview();
    } catch (err) {
      setError(err.message || "No se pudo aprobar el usuario");
    } finally {
      setApprovingId("");
    }
  };

  const totals = overview?.totals || {};

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.5, md: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h3"
            sx={{
              color: BRAND.dark,
              fontSize: { xs: "1.85rem", sm: "2.45rem", md: "3rem" },
              lineHeight: 1.06,
              letterSpacing: { xs: "-.8px", sm: "-1px" },
              fontWeight: 950,
            }}
          >
            Superpanel WebProfit
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.6, fontSize: { xs: ".9rem", sm: "1rem" }, lineHeight: 1.5 }}>
            Tu vista interna para aprobar clientes, controlar proyectos y ordenar el onboarding.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
        >
          <Button
            onClick={() => navigate("/dashboard/actividad")}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Actividad global
          </Button>
          <Button
            onClick={() => navigate("/dashboard/usuarios")}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Usuarios
          </Button>
          <Button
            onClick={() => navigate("/dashboard/proyectos")}
            variant="contained"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Proyectos
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}
      {loading && <Alert severity="info" sx={{ borderRadius: 3 }}>Cargando superpanel...</Alert>}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PeopleAltRoundedIcon />} title="Usuarios" value={loading ? "..." : totals.users || 0} helper="Todos los usuarios registrados en WebProfit." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PendingActionsRoundedIcon />} title="Pendientes" value={loading ? "..." : totals.pendingUsers || 0} helper="Cuentas esperando aprobación." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<StorefrontRoundedIcon />} title="Proyectos" value={loading ? "..." : totals.projects || 0} helper="Clientes/páginas creadas." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<CheckCircleRoundedIcon />} title="Activos" value={loading ? "..." : totals.activeProjects || 0} helper="Proyectos operativos." />
        </Grid>
      </Grid>

      <IntegrityAuditSection
        audit={integrityAudit}
        loading={auditLoading}
        error={auditError}
        onReload={loadIntegrityAudit}
        onNavigate={navigate}
      />

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,0.08)", height: "100%", minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.2, fontWeight: 950, color: BRAND.dark }}>Usuarios pendientes</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.45, fontSize: { xs: ".82rem", sm: ".95rem" }, lineHeight: 1.45 }}>Primero confirman email, después vos los aprobás como cliente.</Typography>
                </Box>
                <Button
                  onClick={() => navigate("/dashboard/usuarios")}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" }, color: BRAND.dark, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                >
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.pendingUsers || []).length === 0 && <Alert severity="success">No hay usuarios pendientes.</Alert>}
                {(overview?.pendingUsers || []).map((user) => (
                  <Box key={user.id} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)", minWidth: 0 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{user.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", overflowWrap: "anywhere" }}>{user.email}</Typography>
                      </Box>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ flexShrink: 0 }}>
                        <Chip
                          size="small"
                          label={user.emailVerified ? "Email ok" : "Sin verificar"}
                          color={user.emailVerified ? "success" : "warning"}
                          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                        />
                        <Button
                          disabled={approvingId === user.id}
                          onClick={() => approveAsClient(user)}
                          variant="contained"
                          sx={{ width: { xs: "100%", sm: "auto" }, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                        >
                          Aprobar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,0.08)", height: "100%", minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.2, fontWeight: 950, color: BRAND.dark }}>Últimos proyectos</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.45, fontSize: { xs: ".82rem", sm: ".95rem" }, lineHeight: 1.45 }}>Páginas/clientes cargados en el sistema.</Typography>
                </Box>
                <Button
                  onClick={() => navigate("/dashboard/proyectos")}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" }, color: BRAND.dark, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                >
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.latestProjects || []).length === 0 && <Alert severity="info">Todavía no hay proyectos.</Alert>}
                {(overview?.latestProjects || []).map((project) => (
                  <Box key={project.id} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)", minWidth: 0 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.25}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{project.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", overflowWrap: "anywhere" }}>/{project.slug} · {project.plan} · {project.status}</Typography>
                      </Box>
                      <Button
                        onClick={() => navigate(`/dashboard/proyectos/${project.slug}`)}
                        sx={{ width: { xs: "100%", sm: "auto" }, alignSelf: { xs: "stretch", sm: "center" }, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                      >
                        Administrar
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
